import { computed, observable, action, isArrayLike } from 'mobx';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { BaseStore } from './BaseStore';
import { DataStore } from './DataStore';
import { TListDisclaimersResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/config/TListDisclaimersResponseBody';
import { Services } from '../services';
import { Config } from '../models/config';
import { CellDisclaimer } from '../models/configuration/explorer/CellDisclaimer';
import { ConditionalHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { CellDisclaimerDisplay } from '../models/configuration/explorer/CellDisclaimerDisplay';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';

interface DisclaimersByFundType {
    [fundType: string]: DisclaimersByModuleId;
}

interface DisclaimersByModuleId {
    [moduleId: string]: DisclaimersByKey;
}

interface DisclaimersByKey {
    [key: string]: Disclaimer;
}

interface Disclaimer {
    key: string;
    type: string;
    text: string;
    superscript?: number;
}

export class DisclaimerStore extends BaseStore {
    @observable.ref rawDisclaimers: TListDisclaimersResponseBody = [];
    @observable.ref disclaimersDictionary: { [key: string]: string };

    @computed
    get dataStore(): DataStore {
        return this.stores && this.stores.data;
    }
    @computed
    get services(): Services {
        return this.dataStore && this.dataStore.services;
    }
    @computed
    get config(): Config {
        return this.dataStore && this.dataStore.config;
    }
    @computed
    get culture(): string {
        return this.dataStore && this.dataStore.culture;
    }
    @computed
    get disclaimersByKey(): DisclaimersByKey {
        if (this.dataStore) {
            const { config } = this.dataStore;
            const fundType = config.selectedFundType.fundType;
            const moduleId = config.selectedModule.id;
            return (
                this.disclaimersByFundType &&
                this.disclaimersByFundType[fundType] &&
                this.disclaimersByFundType[fundType][moduleId]
            );
        }
        return {};
    }
    @observable.ref
    disclaimersByFundType: DisclaimersByFundType = {};

    @computed
    get cellDisclaimerSuperscriptTypes(): Array<CellDisclaimer> {
        const response: Array<CellDisclaimer> = [];
        if (this.dataStore) {
            const { config } = this.dataStore;
            if (config) {
                const { columnConfig } = config;
                response.push(
                    ...this.getCellDisclaimersFromColumnConfig(columnConfig)
                );
            }
        }
        return response;
    }

    getCellDisclaimersFromColumnConfig(columnConfig?: Array<ColumnConfig>) {
        const response: Array<CellDisclaimer> = [];
        if (isArrayLike(columnConfig) && columnConfig.length > 0) {
            columnConfig.forEach(column => {
                if (column) {
                    const { cellDisclaimers, type } = column;
                    if (isArrayLike(cellDisclaimers)) {
                        response.push(
                            ...cellDisclaimers.filter(
                                cellDisclaimer =>
                                    cellDisclaimer.display ===
                                        CellDisclaimerDisplay.superscript ||
                                    !cellDisclaimer.display
                            )
                        );
                    }
                    if (['group', 'dropdown'].includes(type)) {
                        const { groups = [] } = column;
                        groups.forEach(group => {
                            response.push(
                                ...this.getCellDisclaimersFromColumnConfig(
                                    group.columns
                                )
                            );
                        });
                    }
                }
            });
        }
        return response;
    }

    @action
    loadDisclaimers() {
        if (this.dataStore) {
            let disclaimersByKey: DisclaimersByKey = {};
            if (this.disclaimersDictionary) {
                Object.keys(this.disclaimersDictionary).forEach(type => {
                    const text = this.disclaimersDictionary[type];
                    disclaimersByKey = this.addDisclaimerToDisclaimersByKey(
                        disclaimersByKey,
                        type,
                        text
                    );
                });
            }

            const { funds, config } = this.dataStore;
            funds
                .filter(fund => !fund._disclaimersLoaded)
                .forEach(fund => {
                    const { disclaimers: fundDisclaimers = {} } = fund;
                    Object.keys(fundDisclaimers).forEach(type => {
                        const text = this.getDisclaimerTextFromEntity(
                            fund,
                            type
                        );

                        if (text) {
                            disclaimersByKey = this.addDisclaimerToDisclaimersByKey(
                                disclaimersByKey,
                                type,
                                text
                            );
                        }
                    });
                });

            const cellDisclaimers = this.cellDisclaimerSuperscriptTypes;
            cellDisclaimers.forEach(cellDisclaimer => {
                const { conditional, disclaimerType } = cellDisclaimer;
                const conditionalHelper =
                    conditional && new ConditionalHelper(conditional);

                funds
                    .filter(fund => !fund._disclaimersLoaded)
                    .forEach(fund => {
                        const text = this.getDisclaimerTextForType(
                            fund,
                            cellDisclaimer,
                            conditionalHelper
                        );
                        if (text) {
                            const key = this.getDisclaimerKey(
                                disclaimerType,
                                text
                            );
                            if (
                                disclaimersByKey &&
                                disclaimersByKey[key] &&
                                !disclaimersByKey[key].superscript
                            ) {
                                disclaimersByKey[
                                    key
                                ].superscript = this.getNextSuperscript(
                                    disclaimersByKey
                                );
                            }
                        }
                    });
            });
            funds.forEach(fund => {
                fund._disclaimersLoaded = true;
            });
            const currentFundType = config.selectedFundType;
            const currentModule = config.selectedModule;

            if (!this.disclaimersByFundType) {
                this.disclaimersByFundType = {};
            }
            if (!this.disclaimersByFundType[currentFundType.fundType]) {
                this.disclaimersByFundType[currentFundType.fundType] = {};
            }
            this.disclaimersByFundType[currentFundType.fundType][
                currentModule.id
            ] = disclaimersByKey;

            this.disclaimersByFundType = {
                ...this.disclaimersByFundType,
            };
        }
    }

    getNextSuperscript(disclaimersByKey: DisclaimersByKey) {
        const nextSuperscript = Object.values(disclaimersByKey).reduce(
            (max, disclaimer) => {
                const { superscript } = disclaimer;
                if (superscript && superscript >= max) {
                    max = disclaimer.superscript + 1;
                }
                return max;
            },
            1
        );
        return nextSuperscript;
    }

    addDisclaimerToDisclaimersByKey(
        disclaimersByKey: DisclaimersByKey,
        type: string,
        text: string,
        addSuperscript: boolean = false
    ) {
        const key = this.getDisclaimerKey(type, text);
        if (!disclaimersByKey[key]) {
            disclaimersByKey[key] = {
                key,
                type,
                text,
            };
        }
        if (addSuperscript && !disclaimersByKey[key].superscript) {
            disclaimersByKey[key].superscript = this.getNextSuperscript(
                disclaimersByKey
            );
        }
        return disclaimersByKey;
    }

    getDisclaimerKey(type: string, text: string) {
        return `${type}_${text}`;
    }

    // Get disclaimers
    @action
    getGlobalDisclaimers = async () => {
        try {
            if (this.services) {
                this.rawDisclaimers = await this.services.getGlobalDisclaimers(
                    this.config &&
                        this.config.globalConfig &&
                        this.config.globalConfig.disclaimerTypes
                );
                const disclaimers = groupBy(
                    this.rawDisclaimers,
                    'disclaimerType'
                );

                for (const d in disclaimers) {
                    if (d) {
                        const disclaimer =
                            disclaimers[d][0].disclaimers.find(
                                disclaimerContent =>
                                    disclaimerContent.culture === this.culture
                            ) ||
                            disclaimers[d][0].disclaimers.find(
                                disclaimerContent =>
                                    disclaimerContent.culture ===
                                    this.config.defaultCulture
                            );

                        disclaimers[d] = get(disclaimer, 'disclaimer', '');
                    }
                }

                this.disclaimersDictionary = disclaimers;
            }
        } catch (e) {
            console.warn(e);
        }
    };

    getUnlinkedDisclaimers = (disclaimerTypes = []) => {
        const disclaimers = disclaimerTypes
            .map(d => get(this.disclaimersDictionary, d))
            .filter(d => d !== undefined);

        return disclaimers;
    };

    getDisclaimersForDisplay(
        disclaimerTypes: Array<string> = [],
        includeSuperscriptedDisclaimers: boolean = false
    ) {
        const disclaimers: Array<any> = [];
        const disclaimersByType = disclaimerTypes.reduce(
            (acc, disclaimerType) => {
                const text = get(this.disclaimersDictionary, disclaimerType);
                if (text !== undefined) {
                    acc[disclaimerType] = text;
                }
                return acc;
            },
            {}
        );
        const superscriptDisclaimers: Array<string | object> = [];
        if (includeSuperscriptedDisclaimers && this.disclaimersByKey) {
            const orderedDisclaimers = Object.values(this.disclaimersByKey)
                .filter(disclaimer => {
                    const { superscript, type, text } = disclaimer;
                    if (superscript) {
                        if (disclaimersByType[type] === text) {
                            delete disclaimersByType[type];
                        }
                        return true;
                    }
                    return false;
                })
                .sort((a, b) => a.superscript - b.superscript)
                .map(disclaimer => ({
                    character: disclaimer.superscript.toString(),
                    content: disclaimer.text,
                }));

            superscriptDisclaimers.push(...orderedDisclaimers);
        }

        disclaimerTypes.forEach(disclaimerType => {
            if (disclaimersByType[disclaimerType]) {
                disclaimers.push(disclaimersByType[disclaimerType]);
            }
        });
        disclaimers.push(...superscriptDisclaimers);
        return disclaimers;
    }

    getDisclaimerTextFromEntity(entity: any, type: string): string | undefined {
        const { disclaimers: fundDisclaimers = {} } = entity;
        if (fundDisclaimers[type]) {
            const disclaimerForCulture = fundDisclaimers[type].find(
                disclaimer => disclaimer.culture === this.culture
            );
            if (disclaimerForCulture) {
                return disclaimerForCulture.disclaimer;
            }
        }
        return undefined;
    }

    getDisclaimerTextForType(
        entity: any,
        cellDisclaimer: CellDisclaimer,
        existingConditionalHelper?: ConditionalHelper
    ) {
        const { disclaimerType, conditional } = cellDisclaimer;
        if (conditional) {
            const conditionalHelper =
                existingConditionalHelper || new ConditionalHelper(conditional);
            if (!conditionalHelper.matches(entity)) {
                return undefined;
            }
        }
        let text: string | undefined = this.getDisclaimerTextFromEntity(
            entity,
            disclaimerType
        );
        // If there is no disclaimer at the entity level then look at the global disclaimers
        if (!text) {
            text =
                this.disclaimersDictionary &&
                this.disclaimersDictionary[disclaimerType];
        }
        return text;
    }

    getSuperscript(
        entity: any,
        cellDisclaimer: CellDisclaimer
    ): number | undefined {
        const { disclaimerType } = cellDisclaimer;
        const text = this.getDisclaimerTextForType(entity, cellDisclaimer);
        if (!text) {
            return undefined;
        }
        const key = this.getDisclaimerKey(disclaimerType, text);
        return (
            this.disclaimersByKey &&
            this.disclaimersByKey[key] &&
            this.disclaimersByKey[key].superscript
        );
    }

    getSuperscripts(
        entity: any,
        cellDisclaimers: Array<CellDisclaimer>
    ): Array<number> {
        const superscripts = cellDisclaimers
            .filter(
                cellDisclaimer =>
                    cellDisclaimer.display ===
                        CellDisclaimerDisplay.superscript ||
                    !cellDisclaimer.display
            )
            .map(cellDisclaimer => this.getSuperscript(entity, cellDisclaimer))
            .filter(superscript => !!superscript);
        return superscripts;
    }
}



// WEBPACK FOOTER //
// ./src/stores/DisclaimerStore.tsx